/**
 * License Management Hook
 *
 * Handles license activation, deactivation, and status fetching.
 */
import { useState, useEffect, useCallback } from 'react';
export function useLicense() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const config = window.wooAiChatbot || {};
    const restUrl = config.restUrl || '/wp-json';
    const nonce = config.nonce || '';
    const fetchStatus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${restUrl}/woo-ai/v1/license`, {
                headers: {
                    'X-WP-Nonce': nonce,
                },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch license status');
            }
            const data = await response.json();
            setStatus(data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        }
        finally {
            setLoading(false);
        }
    }, [restUrl, nonce]);
    const activate = useCallback(async (key) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${restUrl}/woo-ai/v1/license/activate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce,
                },
                body: JSON.stringify({ license_key: key }),
            });
            const data = await response.json();
            if (!response.ok) {
                return { success: false, error: data.message || 'Activation failed' };
            }
            setStatus(data.data);
            return { success: true };
        }
        catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMsg);
            return { success: false, error: errorMsg };
        }
        finally {
            setLoading(false);
        }
    }, [restUrl, nonce]);
    const deactivate = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${restUrl}/woo-ai/v1/license/deactivate`, {
                method: 'POST',
                headers: {
                    'X-WP-Nonce': nonce,
                },
            });
            const data = await response.json();
            setStatus(data.data);
            return { success: true };
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            return { success: false };
        }
        finally {
            setLoading(false);
        }
    }, [restUrl, nonce]);
    const revalidate = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${restUrl}/woo-ai/v1/license/validate`, {
                method: 'POST',
                headers: {
                    'X-WP-Nonce': nonce,
                },
            });
            const data = await response.json();
            setStatus(data.data);
            return { success: true, is_valid: data.is_valid };
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            return { success: false, is_valid: false };
        }
        finally {
            setLoading(false);
        }
    }, [restUrl, nonce]);
    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);
    return {
        status,
        loading,
        error,
        activate,
        deactivate,
        revalidate,
        refresh: fetchStatus,
    };
}
export default useLicense;
